"use client";

import { useCallback, useEffect, useState } from "react";

const LOCAL_STORAGE_CHANGE_EVENT = "local-storage-change";

interface LocalStorageChangeDetail {
    key: string;
    value: string | null;
}

export function setLocalStorage(
    key: string,
    value: string,
): void {
    if (typeof window === "undefined") return;

    localStorage.setItem(key, value);

    window.dispatchEvent(
        new CustomEvent<LocalStorageChangeDetail>(
            LOCAL_STORAGE_CHANGE_EVENT,
            {
                detail: {
                    key,
                    value,
                },
            },
        ),
    );
}

export function removeLocalStorage(key: string): void {
    if (typeof window === "undefined") return;

    localStorage.removeItem(key);

    window.dispatchEvent(
        new CustomEvent<LocalStorageChangeDetail>(
            LOCAL_STORAGE_CHANGE_EVENT,
            {
                detail: {
                    key,
                    value: null,
                },
            },
        ),
    );
}

export function useLocalStorageValue(
    key: string,
): string | null {
    const readValue = useCallback(() => {
        if (typeof window === "undefined") {
            return null;
        }

        return localStorage.getItem(key);
    }, [key]);

    const [value, setValue] = useState<string | null>(null);

    useEffect(() => {
        setValue(readValue());

        // 다른 탭이나 창에서 변경된 경우
        const handleStorage = (event: StorageEvent) => {
            if (event.storageArea !== localStorage) return;
            if (event.key !== key) return;

            setValue(event.newValue);
        };

        // 현재 탭에서 setLocalStorage/removeLocalStorage를 실행한 경우
        const handleLocalChange = (event: Event) => {
            const customEvent =
                event as CustomEvent<LocalStorageChangeDetail>;

            if (customEvent.detail.key !== key) return;

            setValue(customEvent.detail.value);
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener(
            LOCAL_STORAGE_CHANGE_EVENT,
            handleLocalChange,
        );

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener(
                LOCAL_STORAGE_CHANGE_EVENT,
                handleLocalChange,
            );
        };
    }, [key, readValue]);

    return value;
}